import {TransDto} from "@/common/dto/TransDto";
import {ApiClient} from "@/api/ApiClient";
import {TransFilter} from "@/common/req/TransFilter";
import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {TransSum} from "@/common/dto/TransSum";
import {TransData} from "@/common/dto/TransData";

export function useTransactions(req: TransFilter, enabled: boolean = true) : UseQueryResult<TransDto[]> {
    return useQuery({
        queryKey: ['transactions', JSON.stringify(req)],
        queryFn: () => ApiClient.instance.getTransactions(req),
        enabled,
    });
}

export function useTransactionSummary(fromDate?: string, toDate?: string) : UseQueryResult<Record<number, TransSum>> {
    return useQuery({
        queryKey: ['transactions', 'summary', fromDate + '-' + toDate],
        queryFn: () => ApiClient.instance.getBalances(fromDate, toDate),
    });
}

export function useTransactionAccountSummary(accountId: number, fromDate?: string, toDate?: string) : UseQueryResult<TransSum> {
    return useQuery({
        queryKey: ['transactions', 'account', accountId, 'summary', fromDate + '-' + toDate],
        queryFn: () => ApiClient.instance.getAccountTransactionSum(accountId, fromDate, toDate),
    });
}

export function useCreateTransaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (req: TransData) =>
            ApiClient.instance.createTransaction(req),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
    });
}

export function useUpdateTransaction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({transId, req} : {transId: number, req: TransData}) => ApiClient.instance.updateTransaction(transId, req),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
    });
}

export function useDeleteTransaction() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (transId: number) => ApiClient.instance.deleteTransaction(transId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
        },
    });
}